import { 
    FiBarChart2, 
    FiActivity, 
    FiTrendingUp, 
    FiZap, 
    FiLayers, 
    FiGrid, 
    FiCpu, 
    FiArrowUp, 
    FiArrowDown, 
    FiShield, 
    FiTarget, 
    FiLogOut,
    FiDatabase,
    FiShuffle,
    FiDivideCircle,
    FiGitMerge,
    FiClock
} from 'react-icons/fi';
import { DEFAULT_MARKER_STATES } from '../utils/markerDefaults';

/**
 * Centralized Configuration for Node Visualization & Defaults
 * 
 * This file consolidates styling defaults, initial parameters, and visual behavior 
 * that was previously scattered across StrategyToolbox, useNodeFactory, and ChartStylingSection.
 */

export const CHART_VISUAL_TYPES = ['candle_node', 'line_node', 'area_node', 'heikin_node', 'volume_node', 'bar_node'];

export const INDICATOR_NODE_TYPES = ['indicator', 'math', 'script', 'custom_code'];

//! 50-7 — Node types whose chart presence is a per-bar TRUE/FALSE marker (condition /
//! signal nodes). They are HIDDEN on the chart unless the node opts in: the live
//! Live-Analysis chart loaded with every condition node painted as a labelled dot.
//! Order entry/exit markers come from tradeMarkerBuilder, not from these nodes.
export const CONDITION_MARKER_NODE_TYPES = ['comparator', 'logic_gate', 'signal', 'position_trigger'];

//! 50-7 — ONE authority for "is this node drawn on the chart". Explicit setting wins
//! (`visual_settings.showOnChart`, then `is_visible`); with neither, condition-marker
//! nodes default hidden and everything else default shown. Every eye toggle, the chart
//! derivation and the watch dashboard read this — never re-derive the rule inline.
export const isNodeShownOnChart = (nodeType, data = {}) => {
    const d = data || {};
    if (typeof d.visual_settings?.showOnChart === 'boolean') return d.visual_settings.showOnChart;
    if (typeof d.is_visible === 'boolean') return d.is_visible;
    return !CONDITION_MARKER_NODE_TYPES.includes(nodeType);
};

//! 50-8 (R1-T3, D10) — ONE builder of the node-visibility / pane-placement patch. Every eye toggle
//! (MinimalIndicatorNode, SignalNode, ArithmeticNode, LogicNode, ChartStylingSection header) and the
//! PaneControl overlay toggle build their patch HERE and hand it to the one display writer
//! (`useNodeVisualOverrides().write` / the store's `updateNodeDisplay`); none calls `setNodes` for
//! these keys. `data` is the node's EFFECTIVE data so `isNodeShownOnChart` (the one visibility
//! evaluator above) and the spread `visual_settings` both start from what the user sees.
//!   toggle 'visibility' -> { is_visible, visual_settings: { ...vs, showOnChart[, type] } }
//!   toggle 'overlay'    -> { visual_settings: { ...vs, is_overlay: options.value } }
//! `options.defaultType` seeds `visual_settings.type` only when the node has none (line / marker).
export const buildNodeVisualTogglePatch = (nodeType, data = {}, toggle = 'visibility', options = {}) => {
    const d = data || {};
    const vs = d.visual_settings || {};
    if (toggle === 'overlay') {
        return { visual_settings: { ...vs, is_overlay: !!options.value } };
    }
    const newShow = typeof options.value === 'boolean' ? options.value : !isNodeShownOnChart(nodeType, d);
    return {
        is_visible: newShow,
        visual_settings: {
            ...vs,
            showOnChart: newShow,
            ...(options.defaultType ? { type: vs.type || options.defaultType } : {}),
        },
    };
};

//! 50-7 — ONE authority for "price overlay or sub-pane". The node's own
//! `visual_settings.is_overlay` wins (the Pane toggle writes it), then the backend
//! indicator metadata, then the type default: condition markers and field extractors
//! sit on price; `math` defaults to a SUB-PANE because its value range (e.g. close −
//! WMA, two-digit numbers) differs per use and pinned the price scale when loaded as an
//! overlay (50-4 F-MATHSCALE); an indicator with no metadata keeps the pre-50-7 default
//! (sub-pane) — registered indicators always carry `is_overlay` in their metadata.
export const resolveNodeOverlay = (nodeType, data = {}, backendIsOverlay) => {
    const d = data || {};
    if (typeof d.visual_settings?.is_overlay === 'boolean') return d.visual_settings.is_overlay;
    if (typeof backendIsOverlay === 'boolean') return backendIsOverlay;
    return CONDITION_MARKER_NODE_TYPES.includes(nodeType) || !!d.isFieldExtractor;
};

//! 142-37 — ONE authority for "which outputs of a multi-output indicator are drawn". Meta
//! keys never draw (leading underscore, `direction` (custom overlay), model identity keys).
//! A model indicator (any `p_*` probability output) draws ONLY its probability outputs as
//! separate lines — `vol_level` / `argmax` / magnitude heads stay off by default.
const NON_SERIES_OUTPUT_KEYS = ['direction', 'model_version_id', 'output_schema_version'];
export const getDrawnOutputKeys = (keys = []) => {
    const series = keys.filter(k => !k.startsWith('_') && !NON_SERIES_OUTPUT_KEYS.includes(k));
    const probs = series.filter(k => k.startsWith('p_'));
    return probs.length > 0 ? probs : series;
};

//! 142-37 — history may be MIXED: older cache rows are scalars (pre write-back fix), newer are
//! output dicts. When ANY point is a dict, a scalar point is the node's configured output
//! (`scalarKey`); the other outputs stay absent (a gap), never a pane break.
export const expandScalarPoints = (points, scalarKey) => {
    if (!Array.isArray(points) || !scalarKey) return points;
    const isDict = v => v !== null && typeof v === 'object' && !Array.isArray(v);
    if (!points.some(p => isDict(p?.value))) return points;
    return points.map(p => (typeof p?.value === 'number' ? { ...p, value: { [scalarKey]: p.value } } : p));
};

//! 142-42 — ONE rule for the CHART value of a live WS node result. A multi-output node reduced
//! by its configured `output` ships that scalar as `value` and the full dict as
//! `metadata.outputs` (graph_evaluator 142-37 loop 2). The chart draws the full dict — the same
//! row shape history carries (write-back stores metadata.outputs, /indicators/calculate returns
//! it). Reading `value` alone put the bare `call` (-1/1) on every p_* line from the first live bar.
export const liveNodeChartValue = (result) => {
    if (!result || typeof result !== 'object' || Array.isArray(result)) return result;
    const outputs = result.metadata?.outputs;
    if (outputs && typeof outputs === 'object' && !Array.isArray(outputs) && Object.keys(outputs).length > 0) {
        return outputs;
    }
    return 'value' in result ? result.value : result;
};

// time_window (#140/#141 E5) is boolean-output like comparator/logic_gate — same family.
export const LOGIC_NODE_TYPES = ['logic_gate', 'comparator', 'time_window'];

export const TRIGGER_NODE_TYPES = ['signal', 'position_trigger'];

// Edge stroke colours come from the TARGET node's accent (see StrategyLogicStep
// processedEdges). These provide the widths/dash + the hover/highlight accent.
export const GRAPH_EDGE_STYLES = {
    DEFAULT: { stroke: 'var(--fg-3)', strokeWidth: 1 },
    DATA_TO_INDICATOR: { stroke: 'var(--node-indicator)', strokeWidth: 1.5 },
    DATA_TO_VISUAL: { stroke: 'var(--fg-3)', strokeWidth: 2, opacity: 0.8, strokeDasharray: '5,5' },
    HIGHLIGHT: { stroke: 'var(--color-accent)', strokeWidth: 3, opacity: 1 },
    SUPER_HIGHLIGHT: { stroke: 'var(--color-accent)', strokeWidth: 4, opacity: 1 }
};

export const NODE_VISUAL_CONFIG = {
    // --- Data Sources ---
    'data_source': {
        label: 'Data Source',
        category: 'data',
        description: 'OHLCV, Timeframe',
        icon: FiDatabase,
        colorClass: 'text-orange-500',
        bgClass: 'bg-orange-50 dark:bg-orange-900/20',
        defaultData: { 
            source: 'market', 
            label: 'Data Source',
            timeframe: '5m' // Default
        },
        stylingControls: [] // No visual styling needed
    },

    // --- Visualizations (Chart Types) ---
    'candle_node': {
        label: 'Candlestick',
        category: 'visual',
        description: 'Standard OHLC',
        icon: FiBarChart2,
        colorClass: 'text-emerald-500', 
        bgClass: 'bg-emerald-50 dark:bg-emerald-900/20',
        defaultData: { 
            chartType: 'candle', 
            label: 'Candlestick',
            upColor: '#00C292', 
            downColor: '#E46A76'
        },
        stylingControls: ['candleColors', 'opacity']
    },
    'heikin_node': {
        label: 'Heikin Ashi',
        category: 'visual',
        description: 'Smoothed Trend',
        icon: FiActivity,
        colorClass: 'text-teal-500', 
        bgClass: 'bg-teal-50 dark:bg-teal-900/20',
        defaultData: { 
            chartType: 'heikin', 
            label: 'Heikin Ashi',
            upColor: '#00C292', 
            downColor: '#E46A76'
        },
        stylingControls: ['candleColors', 'opacity']
    },
    'line_node': {
        label: 'Line Chart',
        category: 'visual',
        description: 'Close Price',
        icon: FiTrendingUp,
        colorClass: 'text-blue-500', 
        bgClass: 'bg-blue-50 dark:bg-blue-900/20',
        defaultData: { 
            chartType: 'line', 
            label: 'Line Chart',
            color: '#2196F3',
            lineWidth: 2
        },
        stylingControls: ['chartTypeSelector', 'lineColor', 'lineWidth', 'lineStyle', 'lineType']
    },
    'area_node': {
        label: 'Area Chart',
        category: 'visual',
        description: 'Filled Line',
        icon: FiZap,
        colorClass: 'text-purple-500', 
        bgClass: 'bg-purple-50 dark:bg-purple-900/20',
        defaultData: { 
            chartType: 'area', 
            label: 'Area Chart',
            lineColor: '#2196F3', 
            topColor: 'rgba(33, 150, 243, 0.4)', 
            bottomColor: 'rgba(33, 150, 243, 0.0)'
        },
        stylingControls: ['chartTypeSelector', 'areaColors', 'lineWidth', 'lineStyle', 'lineType']
    },
    'volume_node': {
        label: 'Volume',
        category: 'visual',
        description: 'Volume Bars',
        icon: FiBarChart2,
        colorClass: 'text-orange-500', 
        bgClass: 'bg-orange-50 dark:bg-orange-900/20',
        defaultData: { 
            chartType: 'volume', 
            label: 'Volume',
            upColor: 'rgba(0, 194, 146, 0.5)', 
            downColor: 'rgba(228, 106, 118, 0.5)'
        },
        stylingControls: ['volumeColors', 'opacity']
    },

    // --- Math & Logic ---
    'math': {
        label: 'Arithmetic',
        category: 'math',
        description: 'Add, Sub, Div, Mul',
        icon: FiDivideCircle,
        colorClass: 'text-blue-500', 
        bgClass: 'bg-blue-50 dark:bg-blue-900/20',
        isOverlay: true, // Always an overlay
        defaultData: { 
            op: 'math', 
            label: 'Arithmetic',
            operator: 'add',
            inputs: [
                { type: 'link', offset: 0, id: '0' },
                { type: 'link', offset: 0, id: '1' }
            ],
            // Visual defaults for if/when this is plotted
            color: '#2196F3',
            lineWidth: 2
        },
        stylingControls: ['paneSelector', 'lineColor', 'lineWidth', 'lineStyle', 'lineType']
    },
    'comparator': {
        label: 'Comparison',
        category: 'comparator',
        description: 'Greater, Less, Equal',
        icon: FiShuffle,
        colorClass: 'text-indigo-500', 
        bgClass: 'bg-indigo-50 dark:bg-indigo-900/20',
        isOverlay: true,
        defaultData: { 
            operator: 'gt', 
            label: 'Comparison',
            inputs: [
                { type: 'link', offset: 0, id: '0' },
                { type: 'link', offset: 0, id: '1' }
            ],
            // Markers
            visual_settings: {
                type: 'marker',
                marker_states: DEFAULT_MARKER_STATES
            },
            activeColor: '#10B981', // True/Pass
            inactiveColor: '#EF4444' // False/Fail (optional)
        },
        stylingControls: ['markerSettings'] 
    },
    'logic_gate': {
        label: 'Logical',
        category: 'logic_gate',
        description: 'AND, OR, NOT',
        icon: FiGitMerge,
        colorClass: 'text-purple-500', 
        bgClass: 'bg-purple-50 dark:bg-purple-900/20',
        isOverlay: true,
        defaultData: { 
            operator: 'and', 
            label: 'Logical',
            inputs: [
                { type: 'link', offset: 0, id: '0' },
                { type: 'link', offset: 0, id: '1' }
            ],
            visual_settings: {
                type: 'marker',
                marker_states: DEFAULT_MARKER_STATES
            }
        },
        stylingControls: ['markerSettings']
    },
    // #140/#141 E5 — boolean "now is inside [from, to)" gate in exchange time; the one
    // GraphEvaluator (backend/shared/strategy/graph_evaluator.py, NodeType.TIME_WINDOW) evaluates
    // it identically for backtest/paper/live. `mode` is a builder wiring hint only — the evaluator
    // never branches on it (02-fix-design.md §E4). No inputs; one boolean output like comparator/logic_gate.
    'time_window': {
        label: 'Time Window',
        category: 'time_window',
        description: 'Session-time gate (entry/exit/both)',
        icon: FiClock,
        colorClass: 'text-purple-500',
        bgClass: 'bg-purple-50 dark:bg-purple-900/20',
        isOverlay: true,
        defaultData: {
            mode: 'entry', // 'entry' | 'exit' | 'both'
            from: '09:30',
            to: { anchor: 'square_off', offset_minutes: -15 },
            label: 'Time Window',
            visual_settings: {
                type: 'marker',
                marker_states: DEFAULT_MARKER_STATES
            }
        },
        stylingControls: ['markerSettings']
    },

    // --- Specialized Indicators (Front-end Specific) ---
    'dee_trend_slope': {
        label: 'Dee Trend Slope',
        category: 'indicator',
        // No icon/desc needed here if it's generated dynamically, but good for completeness
        isOverlay: true,
        defaultData: {
            indicator: 'dee_trend_slope',
            // label generated dynamically in factory
            initialVisible: true,
            params: { period: 10, threshold: 0.1, source: 'auto', slope_mode: 'raw', vol_period: 50 },
            visual_settings: { 
                type: 'custom', 
                is_overlay: true, 
                color: '#2196F3',
                marker_states: [
                    { key: 'up', label: 'Up Trend', color: '#10B981', shape: 'arrowUp', position: 'belowBar', enabled: true },
                    { key: 'down', label: 'Down Trend', color: '#EF4444', shape: 'arrowDown', position: 'aboveBar', enabled: true },
                    { key: 'sideways', label: 'Sideways', color: '#9CA3AF', shape: 'circle', position: 'inBar', enabled: true },
                ]
            },
            outputs: ['trend', 'slope', 'angle', 'strength', 'distance', 'ma', 'trend_marker', 'channel_marker', 'marker'],
            outputStyles: {}
        },
        stylingControls: ['deeTrendSlope', 'markerSettings'] // Special custom control + Generic markers
    },
    'position_trigger': {
        label: 'Position Trigger',
        category: 'execution',
        description: 'Entry, Exit, SL, TP',
        icon: FiZap, // Default fallback
        colorClass: 'text-emerald-500', 
        bgClass: 'bg-emerald-50 dark:bg-emerald-900/20',
        defaultData: { 
            action: 'enter_long', 
            label: 'Long Entry',
            visual_settings: {
                type: 'marker', color: '#22c55e',
                marker_states: [
                    { key: 'true', label: 'True', color: '#22c55e', shape: 'arrowUp', position: 'belowBar', enabled: true },
                    { key: 'false', label: 'False', color: '#EF4444', shape: 'arrowDown', position: 'aboveBar', enabled: false },
                ],
                level_display: { enabled: true, mode: 'band', hover_only: false }
                //! trade_markers intentionally omitted — per-action defaults live in
                //  tradeMarkerBuilder.TRADE_MARKER_DEFAULTS (single source of truth).
            }
        },
        stylingControls: ['markerSettings', 'tradeMarkerSettings', 'levelDisplaySettings']
    },
    'signal': {
        label: 'Signal',
        category: 'execution',
        description: 'Custom Signal Action',
        icon: FiActivity,
        colorClass: 'text-indigo-500', 
        bgClass: 'bg-indigo-50 dark:bg-indigo-900/20',
        defaultData: { 
            action: 'custom', 
            label: 'Signal Node',
            visual_settings: {
                type: 'marker', color: '#6366f1',
                marker_states: [
                    { key: 'true', label: 'True', color: '#6366f1', shape: 'arrowUp', position: 'belowBar', enabled: true },
                    { key: 'false', label: 'False', color: '#EF4444', shape: 'arrowDown', position: 'aboveBar', enabled: false },
                ]
                //! trade_markers intentionally omitted — per-action defaults live in
                //  tradeMarkerBuilder.TRADE_MARKER_DEFAULTS (single source of truth).
            }
        },
        stylingControls: ['markerSettings', 'tradeMarkerSettings', 'levelDisplaySettings']
    }
};

// Start Positions for layout (Moving from layoutConfig.js if we want full centralization)
export const LAYOUT_DEFAULTS = {
    paddingY: 24,
    nodeHeights: {
        'data_source': 180,
        'candle_node': 180,
        'indicator': 90,
        'math': 100,
        // ... mapped
    }
};

/**
 * Helper to get config for a node type with fallback
 */
export const getNodeConfig = (type) => {
    return NODE_VISUAL_CONFIG[type] || {};
};

/**
 * Node-classification ACCENT colour (a CSS var) — the single source of truth for
 * the canvas color-coding (left stripe + icon + title + edge). Harmonized,
 * theme-aware via the --node-* / --color-* tokens.
 *  - Structural categories → the categorical --node-* palette.
 *  - execution/signal nodes → by ACTION DIRECTION (semantic profit/loss/warn):
 *    entries & TP that are bullish → profit; short/SL → loss; time/plain exits → warn.
 * NOTE: the action→direction regex is a first pass; refine against real action
 * strings when wiring the signal/position-trigger nodes.
 */
const NODE_CATEGORY_ACCENT = {
    data: 'var(--node-data)',
    visual: 'var(--node-visual)',
    indicator: 'var(--node-indicator)',
    math: 'var(--node-math)',
    logic_gate: 'var(--node-logic)',
    comparator: 'var(--node-comparator)',
    time_window: 'var(--node-logic)',
};

export const getNodeAccent = (type, data = {}) => {
    const config = NODE_VISUAL_CONFIG[type] || {};
    // Fall back to treating `type` itself as the category — the generic 'indicator'
    // node has no config key of its own (only 'dee_trend_slope' does), so without
    // this it would resolve to no category and render a neutral --fg-3 stripe.
    const category = config.category || type;

    if (category === 'execution') {
        const a = String(data.action || data.label || '').toLowerCase();
        if (/take[_ ]?profit|(^|[_ ])tp([_ ]|$)/.test(a)) return 'var(--color-profit)';
        if (/stop[_ ]?loss|(^|[_ ])sl([_ ]|$)/.test(a)) return 'var(--color-loss)';
        if (/enter.*long|long.*entry|(^|[_ ])buy/.test(a)) return 'var(--color-profit)';
        if (/enter.*short|short.*entry|(^|[_ ])sell/.test(a)) return 'var(--color-loss)';
        return 'var(--warning)'; // time exit / plain exits / neutral close
    }

    return NODE_CATEGORY_ACCENT[category] || 'var(--fg-3)';
};

/**
 * Helper to get styling controls for a specific node/data combo
 * This replaces the huge if/else block in ChartStylingSection
 */
export const getStylingControls = (nodeType, nodeData) => {
    const config = NODE_VISUAL_CONFIG[nodeType];
    
    // 1. Direct match
    if (config && config.stylingControls) {
        return config.stylingControls;
    }

    // 2. Dynamic Fallback for Generic Indicators
    if (nodeType === 'indicator') {
        // Special Case: Dee Trend Slope (uses custom internal type)
        const isSlope = nodeData?.indicator === 'dee_trend_slope' || nodeData?.indicator === 'trend_slope';
        if (isSlope) {
            return ['deeTrendSlope', 'markerSettings'];
        }

        const visualSettings = nodeData?.visual_settings || {};
        const type = (visualSettings.type || nodeData?.chartType || 'line').toUpperCase();
        
        // Determine if it's a complex multi-output indicator (e.g. MACD)
        const isMultiOutput = (nodeData?.outputs?.length || 0) > 1;

        let controls = [];

        // 50-7: every plotted indicator can be moved between the price overlay and a
        // sub-pane from its own property (value range differs per use). Marker-only
        // outputs have no pane.
        if (type !== 'MARKER') {
            controls.push('paneSelector');
        }
        
        // Hide plot style selector and global line controls for multi-output indicators
        if (!isMultiOutput) {
            controls.push('chartTypeSelector');
        }

        controls.push('indicatorTheme');

        if (type === 'DUAL_AREA') controls.push('dualAreaColors', 'lineWidth', 'lineStyle', 'lineType');
        else if (type === 'BAND') controls.push('bandColors', 'lineWidth', 'lineStyle', 'lineType');
        else if (type === 'AREA') controls.push('areaColors', 'lineWidth', 'lineStyle', 'lineType');
        else if (type === 'HISTOGRAM') controls.push('histogramColors', 'opacity');
        else if (type === 'MARKER') controls.push('markerSettings');
        else {
            if (!isMultiOutput) {
                controls.push('lineColor', 'lineWidth', 'lineStyle', 'lineType');
            }
        }
        
        return controls;
    }

    return [];
};
